import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';

const RekapBulanan = ({ token }) => {
  const [kelasList, setKelasList] = useState([]);
  const [selectedKelas, setSelectedKelas] = useState('');
  const [selectedPeriode, setSelectedPeriode] = useState(new Date().toISOString().slice(0, 7));
  const [rekapData, setRekapData] = useState([]);
  const [maxMapelPerTanggal, setMaxMapelPerTanggal] = useState({});
  const [loading, setLoading] = useState(false);
  const componentRef = useRef();

  const API_BASE_URL = ''; // Relative path lewat proxy Nginx

  useEffect(() => {
    axios.get(`${API_BASE_URL}/api/kelas`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setKelasList(res.data))
      .catch(err => console.error(err));
  }, [token]);

  const handleCari = async () => {
    if (!selectedKelas) return alert("Pilih kelas dulu!");
    setLoading(true);
    
    try {
      const resAbsen = await axios.get(`${API_BASE_URL}/api/absensi`, {
        params: { bulan: selectedPeriode, kelas: selectedKelas },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const dataAbsenBerstruktur = Array.isArray(resAbsen.data) ? resAbsen.data : [];

      // --- HITUNG MAKSIMAL MAPEL PER TANGGAL SECARA PRESISI (M1 - M8+) ---
      const hitungMaxMapel = {};
      
      dataAbsenBerstruktur.forEach(siswa => {
        const detailHarian = siswa.detail_harian || {};
        
        Object.keys(detailHarian).forEach(tglKey => {
          // Ekstrak angka tanggal dengan aman (Support YYYY-MM-DD, DD, maupun D)
          let tglNum;
          if (typeof tglKey === 'string' && tglKey.includes('-')) {
            tglNum = parseInt(tglKey.split('-')[2], 10);
          } else {
            tglNum = parseInt(tglKey, 10);
          }

          if (isNaN(tglNum)) return;

          const arrayMapel = detailHarian[tglKey];
          if (Array.isArray(arrayMapel)) {
            const totalMapel = arrayMapel.length;
            if (!hitungMaxMapel[tglNum] || totalMapel > hitungMaxMapel[tglNum]) {
              hitungMaxMapel[tglNum] = totalMapel;
            }
          }
        });
      });
      
      setMaxMapelPerTanggal(hitungMaxMapel);

      // --- HITUNG STATISTIK KEHADIRAN SISWA ---
      const processedData = dataAbsenBerstruktur.map(siswa => {
        const detailHarian = siswa.detail_harian || {};
        let stats = { h: 0, s: 0, i: 0, a: 0, d: 0, t: 0 };

        Object.keys(detailHarian).forEach(tgl => {
          const arrayMapel = detailHarian[tgl];
          if (Array.isArray(arrayMapel)) {
            arrayMapel.forEach(item => {
              if (item && item.status) {
                const statusClean = item.status.trim().toLowerCase();
                if (statusClean === 'hadir') stats.h++;
                else if (statusClean === 'sakit') stats.s++;
                else if (statusClean === 'izin') stats.i++;
                else if (statusClean === 'alfa') stats.a++;
                else if (statusClean === 'dispen') stats.d++;
                else if (statusClean === 'terlambat') stats.t++;
              }
            });
          }
        });

        const totalHari = stats.h + stats.s + stats.i + stats.a + stats.d + stats.t;
        const persentase = totalHari > 0 ? Math.round((stats.h / totalHari) * 100) : 0;

        return {
          ...siswa,
          detailHarian,
          stats,
          persentase
        };
      });

      processedData.sort((a, b) => a.nama.localeCompare(b.nama));
      setRekapData(processedData);

    } catch (err) { 
      console.error(err);
      alert('Gagal memuat data. Periksa koneksi API server.'); 
    }
    setLoading(false);
  };

  const [year, month] = selectedPeriode.split('-').map(Number);
  const jsMonth = month - 1; 

  const getDaysArray = () => {
    const daysInMonth = new Date(year, month, 0).getDate(); 
    return Array.from({ length: daysInMonth }, (_, i) => i + 1);
  };
  
  const daysArray = getDaysArray();
  const getDayName = (day) => new Date(year, jsMonth, day).toLocaleDateString('id-ID', { weekday: 'narrow' });

  // Helper mendapatkan slot mapel per tanggal (Minimal 4 slot)
  const getMaxSlotForDay = (d) => {
    const total = maxMapelPerTanggal[d] || maxMapelPerTanggal[String(d)];
    return total && total > 0 ? total : 4; 
  };

  const getStatusColor = (status) => {
    if (!status) return 'transparent';
    const s = status.trim().toLowerCase();
    if (s === 'hadir') return '#dcfce7'; 
    if (s === 'terlambat') return '#ffedd5'; 
    if (s === 'sakit') return '#fef9c3'; 
    if (s === 'izin') return '#dbeafe'; 
    if (s === 'alfa') return '#fee2e2'; 
    if (s === 'dispen') return '#f3e8ff'; 
    return 'transparent';
  };

  const getStatusCode = (status) => {
    if (!status) return '';
    const s = status.trim().toLowerCase();
    if (s === 'hadir') return 'H';
    if (s === 'terlambat') return 'T'; 
    if (s === 'sakit') return 'S';
    if (s === 'izin') return 'I';
    if (s === 'alfa') return 'A';
    if (s === 'dispen') return 'D';
    return '';
  };

  // Helper pencocokan array mapel siswa fleksibel
  const getSiswaMapelArray = (s, d) => {
    if (!s.detailHarian) return [];
    const tglStrPadded = String(d).padStart(2, '0');
    
    // Pencocokan langsung jika key sudah D, DD, atau tglStrPadded
    if (s.detailHarian[d]) return s.detailHarian[d];
    if (s.detailHarian[String(d)]) return s.detailHarian[String(d)];
    if (s.detailHarian[tglStrPadded]) return s.detailHarian[tglStrPadded];

    // Pencocokan jika key berbentuk YYYY-MM-DD
    const keys = Object.keys(s.detailHarian);
    const matchedKey = keys.find(k => {
      if (typeof k === 'string' && k.includes('-')) {
        return parseInt(k.split('-')[2], 10) === d;
      }
      return false;
    });

    return matchedKey ? s.detailHarian[matchedKey] : [];
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadExcel = () => {
    if (rekapData.length === 0) return alert("Tidak ada data untuk diekspor!");
    
    const excelData = rekapData.map((s, index) => {
        const row = { "No": index + 1, "Nama Siswa": s.nama };
        daysArray.forEach(d => {
            const isSunday = new Date(year, jsMonth, d).getDay() === 0;
            if (isSunday) {
                row[`Tgl ${d}`] = 'LIBUR';
            } else {
                const maxMapelHariIni = getMaxSlotForDay(d);
                const arrayMapel = getSiswaMapelArray(s, d);
                
                for (let i = 0; i < maxMapelHariIni; i++) {
                    const dataMapel = arrayMapel && arrayMapel[i];
                    row[`Tgl ${d} - Mpl ${i + 1}`] = dataMapel ? getStatusCode(dataMapel.status) : '-';
                }
            }
        });
        row["H"] = s.stats.h;
        row["T"] = s.stats.t; 
        row["S"] = s.stats.s;
        row["I"] = s.stats.i;
        row["A"] = s.stats.a;
        row["D"] = s.stats.d;
        row["% Kehadiran"] = s.persentase + '%';
        return row;
    });
    
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Rekap Bulanan");
    XLSX.writeFile(workbook, `Rekap_Dinamis_${selectedKelas}_${selectedPeriode}.xlsx`);
  };

  return (
    <div style={{ paddingBottom: '50px' }}>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; padding: 0 !important; margin: 0 !important; }
          .print-area { overflow: visible !important; padding: 0 !important; width: 100% !important; }
          table { font-size: 6px !important; width: 100% !important; border-collapse: collapse !important; }
          th, td { padding: 1px !important; }
          @page { size: landscape; margin: 5mm; }
        }
      `}</style>

      {/* FILTER BAR */}
      <div className="no-print" style={{ marginBottom: '20px' }}>
        <h2 style={{ color: '#1e293b' }}>📅 Laporan Bulanan (Dinamis Mapel)</h2>
        <div style={{ display: 'flex', gap: '10px', background: 'white', padding: '15px', borderRadius: '10px', flexWrap:'wrap' }}>
            <input type="month" value={selectedPeriode} onChange={e => setSelectedPeriode(e.target.value)} style={{ padding: '8px' }} />
            <select value={selectedKelas} onChange={e => setSelectedKelas(e.target.value)} style={{ padding: '8px' }}>
                <option value="">-- Pilih Kelas --</option>
                {kelasList.map(k => <option key={k.id} value={k.nama_kelas}>{k.nama_kelas}</option>)}
            </select>
            <button onClick={handleCari} style={{ background: '#4361ee', color: 'white', padding: '8px 20px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}>{loading ? '...' : 'Tampilkan'}</button>
            <div style={{ marginLeft: 'auto', display:'flex', gap:'5px' }}>
                <button onClick={handleDownloadExcel} style={{ background: '#10b981', color: 'white', padding: '8px 15px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}>📥 Excel</button>
                <button onClick={handlePrint} style={{ background: '#ef4444', color: 'white', padding: '8px 15px', borderRadius: '6px', border: 'none', cursor: 'pointer' }}>🖨️ Cetak / PDF</button>
            </div>
        </div>
      </div>

      {/* AREA TABEL */}
      <div className="print-area" ref={componentRef} style={{ background: 'white', padding: '20px', borderRadius: '10px', overflowX: 'auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '20px', borderBottom: '2px solid #333', paddingBottom:'10px' }}>
            <h3 style={{ margin: 0 }}>REKAPITULASI ABSENSI SISWA MINGGUAN/BULANAN PER MATA PELAJARAN</h3>
            <p style={{ margin: '5px 0 0' }}>Kelas: <b>{selectedKelas || '-'}</b> | Periode: <b>{selectedPeriode}</b></p>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px', border: '1px solid #000' }}>
            <thead>
                <tr style={{ background: '#f8fafc' }}>
                    <th rowSpan="2" style={{ border: '1px solid #000', padding: '5px', minWidth:'20px' }}>NO</th>
                    <th rowSpan="2" style={{ border: '1px solid #000', padding: '5px', minWidth:'130px', textAlign:'left' }}>NAMA SISWA</th>
                    {daysArray.map(d => {
                        const isSunday = new Date(year, jsMonth, d).getDay() === 0;
                        const maxMapelHariIni = getMaxSlotForDay(d);

                        return (
                            <th 
                              key={d} 
                              colSpan={isSunday ? 1 : maxMapelHariIni} 
                              style={{ border: '1px solid #000', textAlign: 'center', background: isSunday ? '#fee2e2' : 'transparent', fontSize:'9px', fontWeight:'bold' }}
                            >
                                {d}<br/><span style={{fontSize:'7px', fontWeight:'normal'}}>{getDayName(d)}</span>
                            </th>
                        );
                    })}
                    <th colSpan="7" style={{ border: '1px solid #000', padding: '5px' }}>TOTAL</th>
                </tr>
                <tr style={{ background: '#f8fafc' }}>
                    {daysArray.map(d => {
                        const isSunday = new Date(year, jsMonth, d).getDay() === 0;

                        if (isSunday) {
                            return <th key={`sub-libur-${d}`} style={{ border: '1px solid #000', fontSize: '7px', background: '#fee2e2', color: '#ef4444', fontWeight:'bold', minWidth:'20px' }}>LIBUR</th>;
                        }

                        const maxMapelHariIni = getMaxSlotForDay(d);
                        const indeksMapelDinamis = Array.from({ length: maxMapelHariIni }, (_, i) => i + 1);

                        return indeksMapelDinamis.map(idx => (
                            <th key={`${d}-${idx}`} style={{ border: '1px solid #000', padding: '2px', textAlign: 'center', fontSize: '7px', background: '#f1f5f9', minWidth: '16px', fontWeight: '500' }}>
                                M{idx}
                            </th>
                        ));
                    })}
                    <th style={{ border: '1px solid #000', width:'20px', background:'#dcfce7' }}>H</th>
                    <th style={{ border: '1px solid #000', width:'20px', background:'#ffedd5' }}>T</th> 
                    <th style={{ border: '1px solid #000', width:'20px', background:'#fef9c3' }}>S</th>
                    <th style={{ border: '1px solid #000', width:'20px', background:'#dbeafe' }}>I</th>
                    <th style={{ border: '1px solid #000', width:'20px', background:'#fee2e2' }}>A</th>
                    <th style={{ border: '1px solid #000', width:'20px', background:'#f3e8ff' }}>D</th>
                    <th style={{ border: '1px solid #000', width:'26px' }}>%</th>
                </tr>
            </thead>
            <tbody>
                {rekapData.length === 0 ? (
                    <tr>
                        <td colSpan={daysArray.reduce((acc, d) => {
                            const isSunday = new Date(year, jsMonth, d).getDay() === 0;
                            return acc + (isSunday ? 1 : getMaxSlotForDay(d));
                        }, 9)} style={{ textAlign: 'center', padding: '20px', fontSize: '12px', fontWeight: '500', color: '#64748b' }}>
                            Belum ada data. Silakan pilih kelas dan klik Tampilkan.
                        </td>
                    </tr>
                ) : (
                    rekapData.map((s, idx) => (
                        <tr key={s.id || idx} style={{ height: '22px' }}>
                            <td style={{ border: '1px solid #000', textAlign: 'center' }}>{idx + 1}</td>
                            <td style={{ border: '1px solid #000', padding: '2px 4px', whiteSpace:'nowrap', fontWeight: '500' }}>{s.nama}</td>
                            
                            {daysArray.map(d => {
                                const arrayMapel = getSiswaMapelArray(s, d);
                                const isSunday = new Date(year, jsMonth, d).getDay() === 0;

                                if (isSunday) {
                                    return (
                                        <td 
                                            key={`sun-${d}`}
                                            style={{ border: '1px solid #000', textAlign: 'center', background: '#fee2e2', color: '#b91c1c', fontSize: '8px', padding: '0px' }}
                                        >
                                            -
                                        </td>
                                    );
                                }

                                const maxMapelHariIni = getMaxSlotForDay(d);
                                const indeksMapelDinamis = Array.from({ length: maxMapelHariIni }, (_, i) => i + 1);

                                return indeksMapelDinamis.map((_, mapelIdx) => {
                                    const dataMapelIni = arrayMapel ? arrayMapel[mapelIdx] : null;
                                    return (
                                        <td 
                                            key={`${d}-${mapelIdx}`} 
                                            style={{ 
                                                border: '1px solid #000', 
                                                textAlign: 'center', 
                                                background: getStatusColor(dataMapelIni?.status), 
                                                fontSize: '8px',
                                                padding: '0px',
                                                fontWeight: 'bold'
                                            }}
                                            title={dataMapelIni ? `${dataMapelIni.mapel} (${dataMapelIni.jam_mulai || ''})` : 'Kosong'}
                                        >
                                            {dataMapelIni ? getStatusCode(dataMapelIni.status) : '-'}
                                        </td>
                                    );
                                });
                            })}

                            <td style={{ border: '1px solid #000', textAlign: 'center', fontWeight:'bold' }}>{s.stats.h}</td>
                            <td style={{ border: '1px solid #000', textAlign: 'center', fontWeight:'bold', color:'#ea580c' }}>{s.stats.t}</td> 
                            <td style={{ border: '1px solid #000', textAlign: 'center' }}>{s.stats.s}</td>
                            <td style={{ border: '1px solid #000', textAlign: 'center' }}>{s.stats.i}</td>
                            <td style={{ border: '1px solid #000', textAlign: 'center', color:'red', fontWeight:'bold' }}>{s.stats.a}</td>
                            <td style={{ border: '1px solid #000', textAlign: 'center', color:'#7e22ce' }}>{s.stats.d}</td>
                            <td style={{ border: '1px solid #000', textAlign: 'center', fontSize:'8px', fontWeight: 'bold' }}>{s.persentase}%</td>
                        </tr>
                    ))
                )}
            </tbody>
        </table>
      </div>
    </div>
  );
};

export default RekapBulanan;